<template>
  <FormGroup>
    <FormGroup>
      <FormField label="Address" :error="form.fieldErrors('location')">
        <AutocompleteAddress v-model="form.fields.location" />
      </FormField>

      <Map :coordinates="coordinates!" />

      <Hint icon="info-circle">
        <View direction="column" gap="sm">
          <div>
            Make sure you have not listed this property for sale as For Rent listings cannot display simultaneously with
            a For Sale on Property platforms.
          </div>
          <div>
            <Link color="accent" target="_blank" :to="advertisingForSaleandForRentListingsSimultaneouslyLink">
              Learn more
            </Link>
          </div>
        </View>
      </Hint>
    </FormGroup>

    <FormGroup>
      <FormField label="Hide address on platforms" direction="row">
        <Switch v-model="form.fields.isAddressHidden" />
      </FormField>

      <Text variant="body-medium" color="secondary">
        Your listing will be published on Property platforms such Zillow. When this switch is on, we’ll only share your
        approximate location and show the address once the Renter requests for a Showing. We do not recommend this.
      </Text>
    </FormGroup>
  </FormGroup>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { ref, watch } from 'vue';

import { FormField, FormGroup, Hint, Link, Switch, Text, View, useForm } from '@tallo/design-system';
import { advertisingForSaleandForRentListingsSimultaneouslyLink } from '@tallo/navigation';
import { AddressDto, Coordinates, PropertyLocationDto, Map } from '@tallo/property';

import AddressService from '../../services/address.service';
import { usePropertyStore } from '../../stores/property.store';
import AutocompleteAddress from '../autocomplete-address.vue';

const coordinates = ref<Coordinates>();
const propertyStore = usePropertyStore();
const form = useForm<{ location: AddressDto | null; isAddressHidden: boolean }>(
  {
    location: null,
    isAddressHidden: false,
  },
  {
    location: { required: helpers.withMessage('Address is required', required) },
  },
);

watch(
  () => form.fields.location,
  async (newValue) => {
    if (newValue && form.fields.location !== propertyStore.property?.location) {
      const address = encodeURI(`${newValue.address}, ${newValue.city}`);
      const details = await AddressService.getAddressDetails(address);

      if (details) {
        coordinates.value = {
          latitude: details.latitude!,
          longitude: details.longitude!,
        };

        form.fields.location!.city = details.city;
        form.fields.location!.zip = details.zip;
        form.fields.location!.state = details.state;
      }
    }
  },
);

async function save() {
  return form.submit(async (fields) => {
    return await propertyStore.saveLocation({
      ...fields.location,
      ...coordinates.value,
      isAddressHidden: fields.isAddressHidden,
    });
  });
}

function populateForm(location: PropertyLocationDto) {
  coordinates.value = {
    latitude: location?.latitude,
    longitude: location?.longitude,
  };

  Object.assign(form.fields, {
    location,
    coordinates: coordinates.value,
    isAddressHidden: location?.isAddressHidden || false,
  });
}

watch(() => propertyStore.property?.location!, populateForm, { immediate: true });

defineExpose({ save, form });
</script>
